/**
 * Supabase Client Configuration
 *
 * This module initializes and exports the Supabase client for use throughout the app.
 */

import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables. Check .env file.');
  console.log('VITE_SUPABASE_URL:', supabaseUrl ? 'set' : 'missing');
  console.log('VITE_SUPABASE_ANON_KEY:', supabaseAnonKey ? 'set' : 'missing');
}

export const supabase = createClient(supabaseUrl || 'https://placeholder.supabase.co', supabaseAnonKey || 'placeholder', {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'implicit',  // Use implicit flow for better cross-browser password reset support
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    storageKey: 'pharmunity-auth-token'
  }
  // Removed custom fetch timeout - was causing auth session fetches to timeout
  // Supabase client has its own timeout handling
});

// Blocked personal email domains
const BLOCKED_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com',
  'qq.com', '163.com', '126.com', 'icloud.com', 'aol.com',
  'protonmail.com', 'mail.com', 'yandex.com', 'live.com',
  'msn.com', 'zoho.com', 'gmx.com', 'fastmail.com'
];

/**
 * Validate corporate email and check against verified domains
 */
export async function validateCorporateEmail(email, userType) {
  // Basic email format check
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { valid: false, error: 'Invalid email format' };
  }

  const domain = email.split('@')[1].toLowerCase();

  // First check if domain exists in database (allows whitelisted personal emails for testing)
  const { data: buyerMatch } = await supabase
    .from('buyers')
    .select('id, company_name')
    .contains('verified_email_domains', [domain]);

  const { data: sellerMatch } = await supabase
    .from('sellers')
    .select('id, company_name')
    .contains('verified_email_domains', [domain]);

  const foundInBuyers = buyerMatch && buyerMatch.length > 0;
  const foundInSellers = sellerMatch && sellerMatch.length > 0;

  // If domain is in database, allow it (even if it's gmail for testing)
  // Otherwise, block personal email domains
  if (!foundInBuyers && !foundInSellers && BLOCKED_DOMAINS.includes(domain)) {
    return {
      valid: false,
      error: 'Personal email addresses are not accepted. Please use your corporate email.',
      isPersonalEmail: true
    };
  }

  // Check based on user type
  if (userType === 'buyer') {
    if (foundInBuyers) {
      return {
        valid: true,
        company: buyerMatch[0],
        foundInBuyers: true,
        foundInSellers,
        sellerCompany: sellerMatch?.[0] || null
      };
    }
  } else if (userType === 'seller') {
    if (foundInSellers) {
      return {
        valid: true,
        company: sellerMatch[0],
        foundInBuyers,
        foundInSellers: true,
        buyerCompany: buyerMatch?.[0] || null
      };
    }
  }

  // Domain not found for the selected role
  return {
    valid: false,
    error: `This email domain is not verified for ${userType} registration.`,
    showContactAdmin: true,
    foundInBuyers,
    foundInSellers
  };
}

/**
 * Password validation - SECURITY: Strong password requirements
 */
export function validatePassword(password) {
  const checks = {
    minLength: password.length >= 12,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  const isValid = Object.values(checks).every(Boolean);
  return { isValid, checks };
}

/**
 * Create user profile after signup
 */
export async function createUserProfile(authUser, userType, companyInfo) {
  let buyerId = null;
  let sellerId = null;
  let isAlsoSeller = false;
  let companyModalities = [];

  if (userType === 'buyer') {
    buyerId = companyInfo.company?.id || null;

    // Check if this company is also a seller (for competitor detection)
    if (companyInfo.foundInSellers && companyInfo.sellerCompany) {
      isAlsoSeller = true;
      // Get modalities of this company as seller
      const { data: assets } = await supabase
        .from('seller_assets')
        .select('modality')
        .eq('seller_id', companyInfo.sellerCompany.id);

      if (assets) {
        companyModalities = [...new Set(assets.map(a => a.modality).filter(Boolean))];
      }
    }
  } else {
    sellerId = companyInfo.company?.id || null;
  }

  const { error } = await supabase
    .from('users')
    .upsert({
      id: authUser.id,
      email: authUser.email,
      user_type: userType,
      buyer_id: buyerId,
      seller_id: sellerId,
      is_also_seller: isAlsoSeller,
      company_modalities: companyModalities,
      subscription_tier: 'free'
    }, { onConflict: 'id' });

  return { error };
}

export default supabase;
